import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../../hooks/useIsMobile';
import axios from "axios";
import { Trash2, SquarePen } from "lucide-react";
import AgGridActionMenu from '../../../components/AgGridAction/AgGridAction';
import { AgGridReact } from 'ag-grid-react';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import TitleCard from '../../../components/TitleCard/TitleCard';
import Loader from '../../../components/Loader/Loader';
import ReusableModal from "../../../components/Modal/ReusableModal";
import CitiesForm from "./CitiesForm";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import MobileCardList from "../../../components/MobileCardList/MobileCardList";
import ActionButton from "../../../components/ActionButton/ActionButton";

const CitiesList = () => {
  const navigate = useNavigate();
  const gridApiRef = useRef(null);

  // Estados UI
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  // Modal de confirmación
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: "confirm",
    title: "Eliminar ciudad",
    message: "¿Seguro que deseas eliminar esta ciudad?",
    onConfirm: null,
  });

    // MOBILE DETECTION
    const isMobile = useIsMobile();

  // Columnas
  const components = useMemo(
    () => ({
      actionMenu: AgGridActionMenu,
    }),
    []
  );

  const [colDefs] = useState([
    { field: "department_code", headerName: "Código Dpto" },
    { field: "department_name", headerName: "Nombre Dpto" },
    { field: "municipal_code", headerName: "Código Municipio" },
    { field: "municipal_name", headerName: "Nombre" },
    { field: "type", headerName: "Tipo" },
    {
      headerName: "Opciones",
      cellRenderer: "actionMenu",
      cellRendererParams: {
        onEdit: (params) => handleEditCity(params),
        onDelete: (params) => handleDeleteCity(params),
      },
      sortable: false,
      filter: false,
      width: 140,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
  ]);

  // COLUMN DEFINITIONS
  const defaultColDef = useMemo(() => ({
    flex: 1,
    filter: false,
    floatingFilter: false,
    editable: false,
    cellStyle: { fontWeight: 'normal' },
  }), []);

  // Datasource
  const createDataSource = useCallback(() => {
    return {
      getRows: async (params) => {
        setLoading(true);
        try {
          const size = params.endRow - params.startRow;
          const page = size > 0 ? params.startRow / size : 0;
          const token = localStorage.getItem("access_token");

          const response = await axios.get(`${process.env.REACT_APP_API_URL}/city/get-all`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { page, size },
          });

          params.successCallback(response.data.data, response.data.total);
        } catch (error) {
          console.error(" Error cargando ciudades:", error);
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    };
  }, []);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;

    //  Forma correcta en versiones nuevas de AG Grid:
    if (typeof params.api.setGridOption === "function") {
      params.api.setGridOption("datasource", createDataSource());
    } else if (typeof params.api.setDatasource === "function") {
      //  Compatibilidad con versiones anteriores
      params.api.setDatasource(createDataSource());
    } else if (params.api.gridOptionsService?.set) {
      // Algunas versiones expone gridOptionsService directamente
      params.api.gridOptionsService.set("datasource", createDataSource());
    }
  }, [createDataSource]);


  const onPaginationChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const api = gridApiRef.current;
    const newPageSize = api.paginationGetPageSize();
    setPageSize((prev) => (prev !== newPageSize ? newPageSize : prev));
  }, []);

  //Acciones
  const handleClickNew = () => navigate('../masters/cities-create');

  const handleEditCity = (params) => {
    const data = params?.rowData || params;
    setSelectedCity(data);
    setShowEditModal(true);
  };

  const handleDeleteCity = (params) => {
    const data = params?.rowData || params;
    const id = data?._id;
    if (!id) return;

    setConfirmModal({
      isOpen: true,
      type: "confirm",
      title: "Eliminar ciudad",
      message: "¿Seguro que deseas eliminar esta ciudad?",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("access_token");
          await axios.delete(`${process.env.REACT_APP_API_URL}/city/delete/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          setConfirmModal({
            isOpen: true,
            type: "success",
            title: "Ciudad eliminada",
            message: "La ciudad fue eliminada correctamente.",
            onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
          });

          if (gridApiRef.current?.purgeInfiniteCache) {
            setLoading(true);
            gridApiRef.current.purgeInfiniteCache();
          } else if (gridApiRef.current?.setGridOption) {
            setLoading(true);
            gridApiRef.current.setGridOption("datasource", createDataSource());
          }
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: "error",
            title: "Error al eliminar",
            message: "No se pudo eliminar la ciudad.",
            onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
          });
        }
      },
    });
  };

  // MOBILE VERSION (cards con paginación al backend)
    const [mobileCity, setMobileCity] = useState([]);
    const [mobilePage, setMobilePage] = useState(0);
    const [mobileTotal, setMobileTotal] = useState(0);
    
    const loadMobileData = useCallback(async () => {
        setLoading(true);
        try {
        const token = localStorage.getItem("access_token");
        const url = `${process.env.REACT_APP_API_URL}/city/get-all`;

        const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            params: { page: mobilePage, size: pageSize },
        });

        setMobileCity(response.data.data);
        setMobileTotal(response.data.total);
        } catch (err) {
        console.error(err);
        } finally {
        setLoading(false);
        }
    }, [mobilePage, pageSize]);

      useEffect(() => {
        if (isMobile) loadMobileData();
      }, [isMobile, loadMobileData]);

    const totalMobilePages = Math.ceil(mobileTotal / pageSize);

  // RENDER
  return (
    <>
      <TitleCard title="Ciudades" />

      {/**Boton crear */}
      <div className="flex justify-end my-1">
        <ActionButton
          onClick={handleClickNew}
        />
      </div>

      {/* DESKTOP: AG GRID */}
      {!isMobile && (
      <div className="relative ag-theme-material my-4" style={{ height: 530, width: '100%' }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
            <Loader message="Cargando Ciudades..." />
          </div>
        )}

        <AgGridReact
          rowModelType="infinite"
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[10, 25, 50]}
          cacheBlockSize={pageSize}
          maxBlocksInCache={5}
          components={components}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onPaginationChanged={onPaginationChanged}
        />
      </div>
        )}

      <MobileCardList
        isMobile={isMobile}
        loading={loading}
        loaderText="Cargando ciudades..."
        data={mobileCity}
        mobilePage={mobilePage}
        setMobilePage={setMobilePage}
        totalPages={totalMobilePages}
        totalItems={mobileTotal}
        pageSize={pageSize}
        renderItem={(city) => (
          <div className="flex">
            <div className="flex-1 grid gap-4 grid-cols-1 [@media(min-width:430px)]:grid-cols-2">
              <h2 className="text-lg font-semibold col-span-full">
                {city.municipal_name}
              </h2>

              <p className="text-sm text-gray-600">{city.type}</p>
              <p className="text-sm text-gray-600">{city.municipal_code}</p>
              <p className="text-sm text-gray-600">{city.department_name}</p>
              <p className="text-sm text-gray-600">{city.department_code}</p>
              <p className="text-sm text-gray-600">Longitud: {city.longitude}</p>
              <p className="text-sm text-gray-600">Latitud: {city.Latitud}</p>


            </div>

            <div className="flex flex-col justify-center items-end space-y-3">
              <button onClick={() => handleEditCity(city)}>
                <SquarePen size={18} color="#16A34A" strokeWidth={1.2} />
              </button>

              <button onClick={() => handleDeleteCity(city)}>
                <Trash2 size={18} color="#B91C1C" strokeWidth={1.2} />
              </button>
            </div>
          </div>
        )}
      />
      
      {/* Modal de edición */}
      <ReusableModal
        isOpen={showEditModal}
        title="Editar Ciudad"
        onClose={() => setShowEditModal(false)}
      >
        <CitiesForm
          initialData={selectedCity}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedCity(null);
            setConfirmModal({
              isOpen: true,
              type: "success",
              title: "Ciudad actualizada",
              message: "Los datos de la ciudad se guardaron correctamente.",
              onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
            });
            if (gridApiRef.current?.purgeInfiniteCache) {
              setLoading(true);
              gridApiRef.current.purgeInfiniteCache();
            } else if (gridApiRef.current?.setGridOption) {
              setLoading(true);
              gridApiRef.current.setGridOption("datasource", createDataSource());
            }
          }}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedCity(null);
          }}
        />
      </ReusableModal>

      {/* Modal de confirmación */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </>
  );
};

export default CitiesList;
